import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import WBFigureView, { isFigureEffectivelyEmpty } from '../components/WBFigure';
import DownloadButton from '../components/DownloadButton';
import { safeText } from '../lib/render';

/**
 * Read an analysis field from either the root (new shape) or from .meta
 * (legacy shape). The single-analysis endpoint historically returned meta
 * nested; the list endpoint flattened it. This shim lets the render code
 * work with either.
 */
function pick(data, key) {
  if (!data) return '';
  if (data[key] != null && data[key] !== '') return data[key];
  if (data.meta && data.meta[key] != null && data.meta[key] !== '') return data.meta[key];
  return '';
}

export default function WorldBankAnalysis() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.get(`/api/worldbank/analysis/${id}`);
        setData(res.analysis);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load analysis');
      }
    })();
  }, [id]);

  const figures = useMemo(() => {
    if (!data) return [];
    const raw = data.figures;
    let list = [];
    if (Array.isArray(raw)) list = raw;
    else if (raw && typeof raw === 'object') list = Object.values(raw);
    return list.filter((f) => !isFigureEffectivelyEmpty(f));
  }, [data]);

  const grouped = useMemo(() => {
    const map = {};
    for (const f of figures) {
      const key = f.section_label || f.section || 'other';
      if (!map[key]) map[key] = [];
      map[key].push(f);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [figures]);

  if (err) {
    return (
      <div className="main-container">
        <div className="alert alert-danger">{err}</div>
        <Link to="/worldbank" className="btn btn-outline-primary">
          <i className="fas fa-arrow-left me-1"></i>Back to list
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="main-container">
        <div className="text-muted">Loading analysis…</div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          <Link to="/worldbank" className="text-muted small">
            <i className="fas fa-arrow-left me-1"></i>Back to World Bank
          </Link>

          <div className="card mt-2 mb-4">
            <div
              style={{
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                color: '#fff',
                padding: '1.4rem',
                borderRadius: '12px 12px 0 0',
              }}
            >
              <div className="mb-2">
                <span
                  className="me-1"
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    borderRadius: 20,
                    padding: '2px 9px',
                    fontSize: '0.75rem',
                  }}
                >
                  <i className="fas fa-globe-africa me-1"></i>
                  {safeText(pick(data, 'country_name') || pick(data, 'country_iso3'))}
                </span>
                <span
                  className="me-1"
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    borderRadius: 20,
                    padding: '2px 9px',
                    fontSize: '0.75rem',
                  }}
                >
                  {safeText(pick(data, 'start_year'))}–{safeText(pick(data, 'end_year'))}
                </span>
                <span
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    borderRadius: 20,
                    padding: '2px 9px',
                    fontSize: '0.75rem',
                  }}
                >
                  {safeText(pick(data, 'n_obs') || (Array.isArray(data.dataset) ? data.dataset.length : ''))} obs
                </span>
              </div>
              <h3 className="mb-0" style={{ fontWeight: 700 }}>
                {safeText(pick(data, 'var1_name'))} <span style={{ opacity: 0.75 }}>vs</span> {safeText(pick(data, 'var2_name'))}
              </h3>
            </div>
            <div className="card-body">
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <DownloadButton
                  href={`/api/worldbank/analysis/${id}/download/pdf`}
                  filename={`WB_Analysis_${id}.pdf`}
                  icon="fa-file-pdf"
                  label="Download PDF"
                  className="btn btn-cta"
                />
                <DownloadButton
                  href={`/api/worldbank/analysis/${id}/download/docx`}
                  filename={`WB_Analysis_${id}.docx`}
                  icon="fa-file-word"
                  label="Download DOCX"
                  className="btn btn-outline-primary"
                />
                <DownloadButton
                  href={`/api/worldbank/analysis/${id}/download/csv`}
                  filename={`WB_Dataset_${id}.csv`}
                  icon="fa-file-csv"
                  label="Download Dataset (CSV)"
                  className="btn btn-outline-secondary"
                />
              </div>
            </div>
          </div>

          {grouped.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
              <p className="text-muted">No figures yet — analysis may still be processing.</p>
            </div>
          ) : (
            grouped.map(([section, figs]) => (
              <section key={section} className="mb-4">
                <h5 className="text-muted text-uppercase small mb-3" style={{ letterSpacing: 2 }}>
                  Section {section}
                </h5>
                <div className="d-flex flex-column gap-3">
                  {figs.map((f) => (
                    <div key={f.figure_id} className="card">
                      <div className="card-body">
                        <WBFigureView fig={f} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}

          {/* Raw values from World Bank — mirrors the same section in the
              PDF/DOCX downloads so every output format ends with the same
              ground-truth data table the statistics were derived from. */}
          {Array.isArray(data.dataset) && data.dataset.length > 0 && (
            <section className="mb-4">
              <h5 className="text-muted text-uppercase small mb-3" style={{ letterSpacing: 2 }}>
                Raw Values from World Bank
              </h5>
              <div className="card">
                <div className="card-body">
                  <p className="text-muted small mb-3">
                    Annual values for <strong>{safeText(data.var1_name)}</strong>{' '}
                    and <strong>{safeText(data.var2_name)}</strong> for{' '}
                    <strong>{safeText(data.country_name)}</strong>{' '}
                    ({safeText(data.start_year)}–{safeText(data.end_year)}),
                    retrieved directly from the World Bank Open Data API.
                    These are the untransformed inputs every statistic above
                    was computed from.
                  </p>
                  <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                    <table
                      style={{
                        borderCollapse: 'collapse',
                        width: '100%',
                        fontSize: 13,
                        tableLayout: 'fixed',
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={rawHeaderStyle}>Year</th>
                          <th style={rawHeaderStyle}>{safeText(data.var1_name)}</th>
                          <th style={rawHeaderStyle}>{safeText(data.var2_name)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.dataset.map((row, i) => (
                          <tr key={i}>
                            <td style={rawCellStyleLabel}>{safeText(row.year)}</td>
                            <td style={rawCellStyleNum}>{fmtRaw(row.var1_value)}</td>
                            <td style={rawCellStyleNum}>{fmtRaw(row.var2_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

const rawHeaderStyle = {
  borderBottom: '2px solid var(--border)',
  padding: '8px 10px',
  textAlign: 'center',
  fontSize: 12.5,
  fontWeight: 600,
};
const rawCellStyleLabel = {
  borderBottom: '1px solid var(--border)',
  padding: '6px 10px',
  textAlign: 'center',
};
const rawCellStyleNum = {
  borderBottom: '1px solid var(--border)',
  padding: '6px 10px',
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
};

function fmtRaw(v) {
  if (v == null || v === '') return '—';
  if (typeof v !== 'number' || !Number.isFinite(v)) return String(v);
  // Keep full precision but avoid scientific notation
  const abs = Math.abs(v);
  if (abs >= 1e6) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1) return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
  return v.toFixed(6);
}
